import { useState } from 'react'
import { Layer } from '../../types'
import { Layers, ChevronDown, Image, Leaf } from 'lucide-react'

interface LayerSwitcherProps {
  layers: Layer[]
  activeLayer: string
  onChange: (layerName: string) => void
}

const layerIcons: Record<string, React.ReactNode> = {
  orthophoto: <Image size={14} />,
  ndvi: <Leaf size={14} className="text-green-500" />,
  ndre: <Leaf size={14} className="text-emerald-500" />,
  gndvi: <Leaf size={14} className="text-lime-500" />,
  savi: <Leaf size={14} className="text-teal-500" />,
}

export default function LayerSwitcher({ layers, activeLayer, onChange }: LayerSwitcherProps) {
  const [open, setOpen] = useState(false)

  const active = layers.find(l => l.layer_name === activeLayer)

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 bg-navy-800 text-white rounded-lg text-sm font-medium hover:bg-navy-700 transition-colors"
      >
        <Layers size={16} />
        <span className="uppercase">{active?.layer_name || 'Layer'}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-100 py-2 w-56 z-50">
          {layers.filter(l => !l.layer_name.endsWith('_raw')).map(layer => (
            <button
              key={layer.layer_name}
              onClick={() => {
                onChange(layer.layer_name)
                setOpen(false)
              }}
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                activeLayer === layer.layer_name ? 'text-orange-500 font-medium bg-orange-50' : 'text-navy-700'
              }`}
            >
              <span className="w-5 flex justify-center">
                {layerIcons[layer.layer_name] || <Image size={14} />}
              </span>
              <span className="uppercase">{layer.layer_name}</span>
              {layer.min_val !== null && layer.max_val !== null && (
                <span className="ml-auto text-xs text-gray-400">
                  {layer.min_val.toFixed(2)}–{layer.max_val.toFixed(2)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
